import React, { useState } from 'react';

import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { getTranslatedNotificationCategory } from 'constants/notification';
import { PAGES } from '../../constants/pages';

import { NotificationCategory, useGetSitesQuery, useSendNotificationToSiteMutation } from 'data/__generated__';

import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { Header, HeaderTitle } from 'components/Header';
import { PageTitle } from 'components/PageTitle';
import { TotemInput } from 'components/TotemInput';
import { TotemTextArea } from 'components/TotemTextArea';
import { Loader, LoaderModeType } from 'components/Loader';
import { TotemSelect } from 'components/TotemSelect';
import { DeeplinkSelector } from './components/DeeplinkSelector';

export const NotificationsSite = () => {
    const [siteId, setSiteId] = useState<string | null>(null);
    const [notificationCategory, setNotificationCategory] = useState<NotificationCategory | null>(null);
    const [deeplink, setDeeplink] = useState<string>();
    const [title, setTitle] = useState<string>('');
    const [body, setBody] = useState<string>('');
    const [slug, setSlug] = useState<string>('');

    const { loading: sitesLoading, data: sitesData, error: sitesError } = useGetSitesQuery();

    const [sendNotificationToSite, { loading: sendNotificationToSiteLoading }] = useSendNotificationToSiteMutation({
        onCompleted: ({ sendNotificationToSite }) => {
            if (sendNotificationToSite) {
                setTitle('');
                setBody('');
                setSlug('');
                toast.success('Les notifications se sont bien envoyées');
            } else {
                toast.error("Une erreur est survenue pendant l'envoi des notifications");
            }
        },
    });

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!siteId) {
            toast.error('Veuillez renseigner le site');
            return;
        }
        if (!title && !body) {
            toast.error('Veuillez renseigner un champ parmi titre et message');
            return;
        }
        if (!slug) {
            toast.error('Veuillez renseigner un identifiant (slug)');
            return;
        }

        await sendNotificationToSite({
            variables: { siteId, title, body, notificationCategory, deeplink, slug },
        });
    };

    if (sitesLoading) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (sitesError || !sitesData) {
        throw new Error('Une erreur est survenue lors de la récupération des sites');
    }

    const sitesOptions = sitesData.sites
        .map((site) => ({ value: site._id, label: site.name }))
        .sort((siteA, siteB) => siteA.label.toLowerCase().localeCompare(siteB.label.toLowerCase()));

    const notificationCategoriesOptions = (
        Object.keys(NotificationCategory) as Array<keyof typeof NotificationCategory>
    ).map((key) => ({
        value: NotificationCategory[key],
        label: getTranslatedNotificationCategory(NotificationCategory[key]),
    }));

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.notifications} />
                </HeaderTitle>

                <Link to="/notifications">
                    <TotemPrimaryButton $isSecondaryStyle>Retour</TotemPrimaryButton>
                </Link>
            </Header>
            <Form onSubmit={handleSubmit}>
                <Message>Choisissez un site pour envoyer une notification aux utilisateurs utilisant ce site</Message>
                {!sitesLoading && sitesData ? (
                    <TotemSelect
                        label="Site"
                        placeholder="Sélectionner un site (recherche par nom ou par _id)"
                        value={siteId}
                        options={sitesOptions}
                        onChange={setSiteId}
                    />
                ) : null}
                <TotemSelect
                    label="Catégorie de notification"
                    placeholder="Sélectionner une catégorie"
                    value={notificationCategory}
                    options={notificationCategoriesOptions}
                    onChange={setNotificationCategory}
                />
                <TotemInput
                    label="Titre de la notif"
                    value={title}
                    placeholder="Titre"
                    onChange={(value) => setTitle(value)}
                />
                <TotemTextArea
                    label="Message de la notif"
                    value={body}
                    placeholder="Message"
                    onChange={(value) => setBody(value)}
                    rows={5}
                />
                <TotemInput
                    label="Identifiant unique (slug)"
                    value={slug}
                    placeholder="daily-delivery"
                    onChange={(value) => setSlug(value)}
                />
                <DeeplinkSelector setDeeplink={setDeeplink} />
                <SubmitButton disabled={sendNotificationToSiteLoading || !siteId || (!title && !body) || !slug}>
                    Envoyer une notif
                </SubmitButton>
                {sendNotificationToSiteLoading ? <Loader mode={LoaderModeType.Spin} /> : null}
            </Form>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 30px;
    overflow-y: scroll;

    & > :not(:first-child) {
        margin-top: 20px;
    }
`;

const SubmitButton = styled(TotemPrimaryButton)`
    margin-top: 20px;
`;

const Message = styled.span`
    text-align: center;
    color: ${({ theme }) => theme.textColor};
`;
